import { useState } from 'react';
import ReactDOM from 'react-dom';
import { Mail, CheckCircle, X, RefreshCw } from 'lucide-react';
import { API_PUBLIC_BASE } from '../utils/supabase/info';
import { devError } from '../utils/logger';


interface EmailVerificationModalProps {
  email: string;
  userType: 'talent' | 'employer';
  onClose?: () => void; // Optional - if not provided, modal cannot be closed
  onRetryLogin: () => void;
  isAfterSignup?: boolean; // If true, don't show close button even if onClose is provided
  loginError?: string; // Error message from login attempt
  isLoggingIn?: boolean; // Loading state from login attempt
}

export function EmailVerificationModal({ email, userType, onClose, onRetryLogin, isAfterSignup = false, loginError = '', isLoggingIn = false }: EmailVerificationModalProps) {
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  const handleResendEmail = async () => {
    setResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      const response = await fetch(
        `${API_PUBLIC_BASE}/resend-verification`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error messages from backend
        if (data.error === 'For mange forsøk. Vennligst vent litt før du prøver igjen.') {
          setResendError('For mange forsøk. Vennligst vent litt før du prøver igjen.');
        } else if (data.error === 'E-posten er allerede bekreftet') {
          setResendError('E-posten er allerede bekreftet. Prøv å logg inn.');
        } else {
          setResendError(data.error || 'Kunne ikke sende e-post på nytt. Prøv igjen senere.');
        }
      } else {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000); // Hide success message after 5s
      }
    } catch (err) {
      devError('Error resending email:', err);
      setResendError('Noe gikk galt. Prøv igjen senere.');
    } finally {
      setResending(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4 pt-safe pb-24 pb-safe overflow-y-auto" style={{ zIndex: 'var(--z-important)' }}>
      <div className="bg-white rounded-3xl shadow-2xl border-2 border-purple-200 max-w-md w-full p-4 sm:p-6 md:p-8 animate-in fade-in slide-in-from-bottom duration-300 relative my-auto max-h-[90vh] overflow-y-auto">
        
        {/* Close button */}
        {onClose && !isAfterSignup && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-purple-50 hover:bg-purple-100 text-purple-600 transition-colors"
            aria-label="Lukk"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Icon */}
        <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-10 h-10 text-purple-600" />
        </div>

        {/* Title */}
        <h2 className="text-2xl text-center text-gray-900 mb-3">
          Bekreft e-postadressen din
        </h2>

        {/* Description */}
        <p className="text-gray-600 text-center mb-6">
          Vi har sendt en bekreftelseslenke til:
        </p>
        
        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-6">
          <p className="text-purple-900 font-medium text-center break-all">
            {email}
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 mb-6 border border-purple-100">
          <p className="text-sm text-gray-700 mb-3 font-medium">
            Følg disse trinnene:
          </p>
          <ol className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="font-bold text-purple-600 flex-shrink-0">1.</span>
              <span>Sjekk innboksen din (og spam-mappen)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-purple-600 flex-shrink-0">2.</span>
              <span>Klikk på bekreftelseslenken i e-posten</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-purple-600 flex-shrink-0">3.</span>
              <span>Kom tilbake hit og logg inn</span>
            </li>
          </ol>
        </div>

        {/* User type specific message */}
        {userType === 'employer' && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Merk:</strong> Som arbeidsgiver må du også vente på godkjenning fra admin etter at du har bekreftet e-posten.
            </p>
          </div>
        )}

        {/* Success message */}
        {resendSuccess && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3 animate-in fade-in slide-in-from-top duration-300">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-green-800 text-sm">
              E-post sendt på nytt! Sjekk innboksen din.
            </p>
          </div>
        )}

        {/* Error message from resend */}
        {resendError && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4">
            <p className="text-red-700 text-sm">{resendError}</p>
          </div>
        )}

        {/* Error message from login attempt */}
        {loginError && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4">
            <p className="text-red-700 text-sm">{loginError}</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {/* Retry login button */}
          <button
            onClick={onRetryLogin}
            disabled={isLoggingIn}
            className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoggingIn ? 'Logger inn...' : 'Jeg har bekreftet - logg inn nå'}
          </button>

          {/* Resend email button */}
          <button
            onClick={handleResendEmail}
            disabled={resending || resendSuccess}
            className="w-full py-3 px-4 bg-white border-2 border-purple-200 text-purple-600 rounded-xl hover:bg-purple-50 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {resending ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Sender...
              </>
            ) : resendSuccess ? (
              <>
                <CheckCircle className="w-4 h-4" />
                E-post sendt!
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Send e-post på nytt
              </>
            )}
          </button>

          {/* Cancel button */}
          {onClose && !isAfterSignup && (
            <button
              onClick={onClose}
              className="w-full py-3 px-4 text-gray-600 hover:text-gray-800 transition-colors font-medium"
            >
              Avbryt
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
